/** @jsx jsx */
import { jsx } from '@emotion/react';
import { FocusEvent } from 'react';
import { Form, FormInstance, FormRule, Input, InputProps } from 'antd';
import { flatten, get } from 'lodash';
import { AIcon as Icon } from '../icon';
import colors from '../colors';
import { normalizeSerialNumber } from '../../utils/serial-number';

// AER-1795: equipment serial numbers are entered on Manufacturing Process and on Edit
// Equipment. Both used to carry their own copy of the blur handler, the ✓/✗ suffix and the
// error plumbing, and their rules drifted apart (AER-1597 changed one screen only). This is
// the one implementation; each screen passes in only what differs: its rules (a
// SerialValidator, item key `validateSerial`) and what to do once a blur check has
// finished (`onSerialValidated`).
//
// Validation runs as a Form.Item rule, so the form owns the error: it is re-checked on
// blur, while the field shows an error, and on submit (validateFields). Errors pushed in
// with setFields were wiped by the next rule run and never blocked a save.

// Returns the error messages for a normalized serial; [] means valid.
export type SerialValidator = (serial: string) => string[] | Promise<string[]>;

// The serial the field's rule checked last. A ✓/✗ is only shown while that is still the
// value on screen, so a stale result never sits next to freshly typed or reset input.
export interface SerialCheck {
  value?: string;
}

// Rejects with every message, so the field lists all that is wrong at once (one line each).
export const serialNumberRule = (
  validate: SerialValidator,
  check: SerialCheck
): FormRule => ({
  // Only on blur, never per keystroke: the shared <Form> re-validates an errored field on
  // every change (AER-1667 R3), which would re-judge a half-typed serial character by
  // character. Validations that name no trigger (submit, the model-change re-check) still
  // run it. While typing, the field's other rules run and clear the stale serial error.
  validateTrigger: 'onBlur',
  validator: (_rule: unknown, value: unknown) => {
    const serial = normalizeSerialNumber(value);
    check.value = serial;
    // An empty field is the `required` rule's call.
    if (!serial) {
      return Promise.resolve();
    }
    return Promise.resolve(validate(serial)).then((errors) =>
      errors && errors.length
        ? Promise.reject(errors.map((message) => new Error(message)))
        : undefined
    );
  },
});

// Validates one field and resolves to its error messages, or to undefined when a newer
// validation superseded this one (its result would be stale).
export const validateSerialField = (
  form: Pick<FormInstance, 'validateFields'>,
  name: string
): Promise<string[] | undefined> =>
  form.validateFields([name]).then(
    () => [] as string[],
    (err: unknown) =>
      get(err, 'outOfDate')
        ? undefined
        : (flatten(
            (
              get(err, 'errorFields', []) as unknown as { errors: string[] }[]
            ).map((f) => f.errors)
          ) as string[])
  );

interface IProps extends Omit<InputProps, 'value' | 'suffix' | 'name'> {
  // injected by Form.Item
  value?: string;
  // the Form.Item's field name
  name: string;
  check: SerialCheck;
  onValidated?: (serial: string, errors: string[]) => void | Promise<void>;
}

export const SerialNumberInput = (props: IProps) => {
  const { value, onBlur, name, check, onValidated, ...rest } = props;
  const form = Form.useFormInstance();
  const { status } = Form.Item.useStatus();

  const serial = normalizeSerialNumber(value);
  const isChecked = !!serial && check.value === serial;

  // Always render a suffix node: AntD re-mounts the input when the suffix appears or
  // disappears, which drops focus mid-keystroke (AER-1657).
  let suffix = <span />;
  if (isChecked && status === 'validating') {
    suffix = <Icon type="loading" />;
  } else if (isChecked && status === 'error') {
    suffix = <Icon type="close-circle" style={{ color: colors.error }} />;
  } else if (isChecked && status === 'success') {
    suffix = <Icon type="check-circle" style={{ color: colors.success }} />;
  }

  const handleBlur = async (e: FocusEvent<HTMLInputElement>) => {
    if (serial !== (value || '')) {
      // Store the normalized serial before the blur-triggered rule reads it. Written to
      // the store directly so the screen's onChange (QR-scan parsing) never sees a
      // synthetic event.
      form.setFieldValue(name, serial);
    }
    if (onBlur) {
      // Form.Item's wrapper: runs the blur-triggered validation.
      onBlur(e);
    }
    if (!onValidated || !serial) {
      return;
    }
    const errors = await validateSerialField(form, name);
    if (errors) {
      await onValidated(serial, errors);
    }
  };

  return <Input {...rest} value={value} onBlur={handleBlur} suffix={suffix} />;
};
